import { useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Divider } from '@hivebrite/design-system/Divider';
import { PillsList } from '@hivebrite/design-system/PillsList';
import type { UseBreakpointProps } from '@hivebrite/design-system/Theme';
import { useBreakpoint } from '@hivebrite/design-system/Theme';

import type { PublicationLayoutsProps } from 'hb-builder/components/BuilderComponents/dynamic/Publication/PublicationLayouts';
import type { News } from 'hb-builder/components/BuilderComponents/dynamic/types/news';
import PublicationNewsLoader from 'hb-builder/components/common/LoadingComponents/PublicationNewsLoader';
import { goToModuleDetailPage } from 'hb-builder/utils/goToModuleDetailPage';
import PublicationText from 'hb-react/shared/components/Publication/PublicationText';

export type NewsWrapperProps = {
  data: News;
  isBackOffice: boolean;
  isLoading: boolean;
  whiteLabelUrl?: string | null;
} & Omit<PublicationLayoutsProps, 'app' | 'isPublic' | 'type'>;

const NewsWrapper = ({
  data: fetchedNews,
  dynamicAttributes,
  ids,
  isBackOffice,
  isLoading,
  navigate,
  whiteLabelUrl,
}: NewsWrapperProps) => {
  const breakpoint = useBreakpoint();
  const [isHover, setIsHover] = useState(false);

  if (isLoading) {
    return <PublicationNewsLoader />;
  }

  const {
    displayCategories,
    displayContent,
    displayCoverPicture,
    displayTitle,
  } = dynamicAttributes?.fields || {};

  const noCategories = !displayCategories || fetchedNews.tags.length === 0;
  const noTitle = !displayTitle;
  // get mobile margin to display img in all the width.
  const MOBILE_CONTENT_MARGIN = 16;

  return (
    <WrapperStyled>
      {displayCoverPicture && (
        <ImgWrapper
          data-testid="builder-cover-wrapper"
          mx={breakpoint === 's' ? `-${MOBILE_CONTENT_MARGIN}px` : 'unset'}
          onClick={() =>
            ids &&
            navigate &&
            goToModuleDetailPage({
              ids,
              isBackOffice,
              navigate,
              news: fetchedNews,
              whiteLabelUrl,
            })
          }
          onMouseEnter={() => setIsHover(true)}
          onMouseLeave={() => setIsHover(false)}
          width={
            breakpoint === 's'
              ? `calc(100% + ${MOBILE_CONTENT_MARGIN * 2}px)`
              : '100%'
          }
          $breakpoint={breakpoint}
        >
          <StyledImg
            as="img"
            src={fetchedNews.cover_picture}
            alt=""
            $isHover={isHover}
          />
        </ImgWrapper>
      )}
      <ContentWrapper pt={{ _: 4, l: noTitle && noCategories ? 6 : 4 }}>
        {displayTitle && (
          <StyledBox mb={noCategories ? 0 : 2}>
            <Box flexDirection="row" justifyContent="space-between">
              <TitleStyled
                onClick={() =>
                  ids &&
                  navigate &&
                  goToModuleDetailPage({
                    ids,
                    isBackOffice,
                    navigate,
                    news: fetchedNews,
                    whiteLabelUrl,
                  })
                }
                onMouseEnter={() => setIsHover(true)}
                onMouseLeave={() => setIsHover(false)}
              >
                {fetchedNews.subject}
              </TitleStyled>
            </Box>
            <DateStyled>
              {__('Published on %{date}').i__({
                date: fetchedNews.published_at,
              })}
            </DateStyled>
          </StyledBox>
        )}
        {!noCategories && (
          <PillsList
            pills={fetchedNews.tags}
            seeLessLabel={s__('Tags|See less')}
            seeMoreLabel={s__('Tags|See more')}
          />
        )}
        {displayContent && (
          <>
            <Divider
              mt={noTitle && noCategories ? 0 : noCategories ? 4 : 3}
              mb={4}
            />
            <PublicationText text={fetchedNews.content} />
          </>
        )}
      </ContentWrapper>
    </WrapperStyled>
  );
};

export default NewsWrapper;

const ContentWrapper = styled(Box).attrs(() => ({
  color: 'primary',
  display: 'block',
  pb: { _: 6, l: 8 },
}))`
  p {
    margin-bottom: 20px;
  }
`;

const DateStyled = styled(Box).attrs({
  font: 'body-regular-asphalt',
  display: 'block',
})``;

const ImgWrapper = styled(Box).attrs({
  overflow: 'hidden',
})<{ $breakpoint: UseBreakpointProps }>`
  border-radius: ${({ theme, $breakpoint }) =>
    $breakpoint === 's'
      ? '0px'
      : `${theme.themeVariables.globalBorderRadius}px`};
  background: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.6));
`;

const StyledImg = styled(Box)<{ $isHover: boolean }>`
  cursor: pointer;
  transition: transform 0.3s;
  transform: ${({ $isHover }) => ($isHover ? 'scale(1.05)' : 'scale(1)')};
  width: ${(props) => props.width || '100%'};
`;

const StyledBox = styled(Box).attrs({
  display: 'block',
})``;

const TitleStyled = styled(Box).attrs({
  font: 'h1-semibold',
  mb: {
    l: '6px',
  },
  display: 'block',
})`
  cursor: pointer;

  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.colors.link} !important;
  }
`;

const WrapperStyled = styled(Box).attrs(() => ({
  pt: { _: 5, l: 12 },
  flexDirection: 'column',
}))``;
